/**
 * @file CostsTab.tsx
 * @brief Super Admin → Analytics → Costs by vendor (W22), from B25 `costs`.
 *
 * @details
 * Vendor cost over time (stacked bars, colour fixed per vendor), the vendor
 * table with models and where each price comes from, cost per product split
 * by who paid for the use (Free / plan / charged), and the data-quality
 * notes the API sends. Infra and Stripe fees are not in any cost: said once,
 * in words, from `not_in_cost`.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { Fragment, useMemo } from "react";
import { GridCell } from "@/features/analytics/components/GridBlock";
import { SkeletonRows } from "@/components/ui/Skeleton";
import {
  COMPONENT_LABEL,
  COVERAGE_LABEL,
  formatCount,
  formatMoney,
  formatMoneyTick,
  formatPct,
  periodLabel,
  pick,
  VENDOR_COLOR,
  VENDOR_LABEL,
  vendorsWithCost,
} from "../businessMath";
import type { Costs, Currency } from "../types";
import { BarsOverTime, ChartLegend, type ChartSeries } from "../components/charts";
import { DataTable, EmptyNote, ErrorNote, MoneyText, SectionHeading, tdClass, tdNumClass } from "../components/common";

const COVERAGES = ["free", "plan", "charged", "other", "not_applicable"] as const;

export function CostsTab({
  data,
  isLoading,
  error,
  currency,
}: {
  data: Costs | undefined;
  isLoading: boolean;
  error: unknown;
  currency: Currency;
}) {
  const vendors = useMemo(() => vendorsWithCost(data?.vendors ?? []), [data]);
  const series: ChartSeries[] = vendors.map((v) => ({
    key: v.vendor,
    label: VENDOR_LABEL[v.vendor] ?? v.vendor,
    color: VENDOR_COLOR[v.vendor] ?? "var(--viz-other)",
  }));
  const rows = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({
        label: periodLabel(s.period, data!.window.groupBy),
        total: pick(s.total, currency),
        ...Object.fromEntries(vendors.map((v) => [v.vendor, Math.max(0, pick(s.by_vendor[v.vendor], currency) ?? 0)])),
      })),
    [data, vendors, currency],
  );
  const total = pick(data?.total, currency) ?? 0;
  const coverageUsed = useMemo(
    () => COVERAGES.filter((c) => (data?.products ?? []).some((p) => p.by_coverage.some((x) => x.coverage === c && x.cost.usd !== 0))),
    [data],
  );

  if (error) return <ErrorNote error={error} what="costs" />;

  const dq = data?.data_quality;
  const preStep = Object.entries(dq?.pre_step_ledger ?? {}).filter(([, m]) => m.usd !== 0);
  const productLabel = (key: string) => data?.products.find((p) => p.product === key)?.label ?? key;

  return (
    <>
      <GridCell>
        <SectionHeading
          title="Vendor cost over time"
          help={
            <>
              Total in this period:{" "}
              <MoneyText minor={pick(data?.total, currency)} currency={currency} className="font-medium text-foreground" />. Vendor
              cost (COGS) as priced in the ledgers, in US cents, dated when the work ran; £ uses the one FX rate above. Our own
              accounts and dry runs are left out.
            </>
          }
          right={series.length > 1 ? <ChartLegend series={series} /> : null}
        />
        <div className="mt-3">
          <BarsOverTime
            data={rows}
            series={series}
            format={(v) => formatMoney(v, currency)}
            tick={(v) => formatMoneyTick(v, currency)}
            isLoading={isLoading}
            isEmpty={vendors.length === 0}
            emptyText="No vendor cost in this period."
            footer={(row) => (
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Total</span>
                <span className="font-medium tabular-nums">{formatMoney(row.total as number | null, currency)}</span>
              </div>
            )}
          />
        </div>
        {data ? (
          <p className="mt-3 text-xs text-muted-foreground">
            Not in any cost here. Infrastructure: {data.not_in_cost.infrastructure}. Payment fees:{" "}
            {data.not_in_cost.payment_fees}.
          </p>
        ) : null}
      </GridCell>

      <GridCell padded={false}>
        <div className="px-4 py-4 md:px-5">
          <SectionHeading title="By vendor" help="Each vendor's models, and where its price comes from." />
        </div>
        {isLoading ? (
          <SkeletonRows />
        ) : vendors.length === 0 ? (
          <EmptyNote>No vendor cost in this period.</EmptyNote>
        ) : (
          <DataTable
            caption="Cost by vendor and model"
            head={[
              { label: "Vendor / model" },
              { label: "Cost", align: "right" },
              { label: "Share", align: "right" },
              { label: "Price from", className: "min-w-[14rem]" },
            ]}
          >
            {vendors.map((v) => {
              const c = pick(v.cost, currency) ?? 0;
              return (
                <Fragment key={v.vendor}>
                  <tr className="border-t border-border/60 align-top">
                    <td className={tdClass}>
                      <span className="flex items-center gap-2 font-medium">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: VENDOR_COLOR[v.vendor] }} aria-hidden />
                        {VENDOR_LABEL[v.vendor] ?? v.vendor}
                      </span>
                    </td>
                    <td className={tdNumClass}>
                      <MoneyText minor={c} currency={currency} />
                    </td>
                    <td className={tdNumClass}>{total > 0 ? formatPct(Math.round((c / total) * 10000) / 100) : "–"}</td>
                    <td className={`${tdClass} text-xs text-muted-foreground`}>{v.price_source}</td>
                  </tr>
                  {v.by_model
                    .filter((m) => m.cost.usd !== 0)
                    .sort((a, b) => b.cost.usd - a.cost.usd)
                    .map((m) => (
                      <tr key={`${v.vendor}-${m.model ?? "none"}`} className="text-xs text-muted-foreground">
                        <td className={`${tdClass} pl-9 font-mono md:pl-10`}>{m.model ?? "(no model)"}</td>
                        <td className={tdNumClass}>
                          <MoneyText minor={pick(m.cost, currency)} currency={currency} />
                        </td>
                        <td colSpan={2} />
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </DataTable>
        )}
      </GridCell>

      <GridCell padded={false}>
        <div className="px-4 py-4 md:px-5">
          <SectionHeading
            title="Cost per product, by who paid"
            help="Free users = use on the Free plan. Covered by a plan = minutes from a plan or an Enterprise pool. Charged = paid for on its own. Ask AI and medical assessments are not applicable."
          />
        </div>
        {isLoading ? (
          <SkeletonRows />
        ) : !data || data.products.every((p) => p.cost.usd === 0) ? (
          <EmptyNote>No product cost in this period.</EmptyNote>
        ) : (
          <DataTable
            caption="Cost per product split by coverage"
            head={[
              { label: "Product" },
              { label: "Cost", align: "right" },
              ...coverageUsed.map((c) => ({ label: COVERAGE_LABEL[c], align: "right" as const })),
              { label: "Vendors", className: "min-w-[10rem]" },
            ]}
          >
            {data.products.map((p) => (
              <tr key={p.product} className="border-t border-border/60 align-top">
                <td className={tdClass}>
                  <span className="font-medium">{p.label}</span>
                  {p.by_component.some((c) => c.cost.usd !== 0) ? (
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {p.by_component
                        .filter((c) => c.cost.usd !== 0)
                        .map((c) => `${COMPONENT_LABEL[c.component] ?? c.component} ${formatMoney(pick(c.cost, currency), currency)}`)
                        .join(" · ")}
                    </span>
                  ) : null}
                </td>
                <td className={tdNumClass}>
                  <MoneyText minor={pick(p.cost, currency)} currency={currency} />
                </td>
                {coverageUsed.map((c) => {
                  const m = p.by_coverage.find((x) => x.coverage === c)?.cost;
                  return (
                    <td key={c} className={tdNumClass}>
                      {m && m.usd !== 0 ? <MoneyText minor={pick(m, currency)} currency={currency} /> : <span className="text-muted-foreground">–</span>}
                    </td>
                  );
                })}
                <td className={`${tdClass} text-xs text-muted-foreground`}>
                  {p.by_vendor.length ? p.by_vendor.map((v) => VENDOR_LABEL[v.vendor] ?? v.vendor).join(", ") : "–"}
                </td>
              </tr>
            ))}
          </DataTable>
        )}
      </GridCell>

      {dq ? (
        <GridCell>
          <SectionHeading title="Data quality" help="What the API says about the numbers above." />
          <ul className="mt-3 space-y-2 text-xs leading-relaxed text-muted-foreground">
            {preStep.length ? (
              <li>
                <span className="font-medium text-foreground">Cost from before step rows (13 Oct 2026): </span>
                {preStep.map(([k, m]) => `${productLabel(k)} ${formatMoney(pick(m, currency), currency)}`).join(", ")}.{" "}
                {dq.pre_step_ledger_note}
              </li>
            ) : null}
            {dq.unattributed_vendor.usd !== 0 ? (
              <li>
                <span className="font-medium text-foreground">No vendor named: </span>
                {formatMoney(pick(dq.unattributed_vendor, currency), currency)} is shown as Unattributed.
              </li>
            ) : null}
            {dq.negative_rows ? (
              <li>
                <span className="font-medium text-foreground">Negative ledger rows: </span>
                {formatCount(dq.negative_rows)} (kept as recorded).
              </li>
            ) : null}
            <li>
              <span className="font-medium text-foreground">Unpriced services: </span>
              {dq.unpriced_services == null
                ? "– (a ledger table has no unpriced column, so this can't be checked)."
                : dq.unpriced_services.length === 0
                  ? "none in this period."
                  : dq.unpriced_services.map((u) => `${u.service} (${formatCount(u.rows)} rows)`).join(", ")}
            </li>
            {dq.interpretation_vendor_note ? <li>{dq.interpretation_vendor_note}</li> : null}
            {dq.deleted_units ? <li>{dq.deleted_units}</li> : null}
          </ul>
        </GridCell>
      ) : null}
    </>
  );
}
